export interface ServiceAccountCredentials {
  type: string;
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
}

export type SheetRow = Record<string, string>;

interface AccessTokenCache {
  token: string;
  expiresAt: number;
}

let tokenCache: AccessTokenCache | null = null;

export async function getAccessToken(creds: ServiceAccountCredentials): Promise<string> {
  if (tokenCache && tokenCache.expiresAt > Date.now()) {
    return tokenCache.token;
  }

  const base64url = (str: string) => {
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const header = base64url(JSON.stringify({ alg: 'RS256', typ: 'JWT' }));

  const now = Math.floor(Date.now() / 1000);
  const claim = base64url(JSON.stringify({
    iss: creds.client_email,
    scope: 'https://www.googleapis.com/auth/spreadsheets',
    aud: 'https://oauth2.googleapis.com/token',
    exp: now + 3600,
    iat: now,
  }));

  const signatureInput = `${header}.${claim}`;

  const privateKeyPem = creds.private_key
    .replace(/-----BEGIN PRIVATE KEY-----/g, '')
    .replace(/-----END PRIVATE KEY-----/g, '')
    .replace(/\s/g, '');

  const binaryKey = Uint8Array.from(atob(privateKeyPem), c => c.charCodeAt(0));

  const key = await crypto.subtle.importKey(
    'pkcs8',
    binaryKey.buffer,
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const encoder = new TextEncoder();
  const signatureBuffer = await crypto.subtle.sign(
    'RSASSA-PKCS1-v1_5',
    key,
    encoder.encode(signatureInput)
  );

  const signatureArray = new Uint8Array(signatureBuffer);
  const signature = btoa(String.fromCharCode(...signatureArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const jwt = `${signatureInput}.${signature}`;

  const response = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `grant_type=urn%3Aietf%3Aparams%3Aoauth%3Agrant-type%3Ajwt-bearer&assertion=${jwt}`,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error_description || data.error || 'Failed to get access token');
  }

  tokenCache = {
    token: data.access_token,
    expiresAt: Date.now() + (data.expires_in - 60) * 1000,
  };

  return data.access_token;
}

export function clearTokenCache() {
  tokenCache = null;
}

export async function fetchSheetRows(
  accessToken: string,
  spreadsheetId: string,
  sheetName: string
): Promise<SheetRow[]> {
  const response = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Failed to fetch ${sheetName}`);
  }

  const data = await response.json();
  const rows = data.values as string[][] | undefined;

  if (!rows || rows.length === 0) {
    return [];
  }

  const headers = rows[0].map(h => h.toLowerCase().trim());
  const dataRows = rows.slice(1);

  return dataRows.map(row => {
    const obj: SheetRow = {};
    headers.forEach((header, index) => {
      obj[header] = row[index] || '';
    });
    return obj;
  });
}

export async function appendSheetRows(
  accessToken: string,
  spreadsheetId: string,
  sheetName: string,
  rows: SheetRow[],
  headers: string[]
): Promise<void> {
  if (rows.length === 0) return;

  const values = rows.map(row => headers.map(h => row[h] || ''));

  const response = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}:append?valueInputOption=RAW&insertDataOption=INSERT_ROWS`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ values }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Failed to append to ${sheetName}`);
  }
}

export async function updateSheetRow(
  accessToken: string,
  spreadsheetId: string,
  sheetName: string,
  rowNumber: number,
  row: SheetRow,
  headers: string[]
): Promise<void> {
  const values = [headers.map(h => row[h] || '')];

  const response = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!A${rowNumber}:${String.fromCharCode(64 + headers.length)}${rowNumber}?valueInputOption=RAW`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ values }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Failed to update row in ${sheetName}`);
  }
}

export async function findRowById(
  accessToken: string,
  spreadsheetId: string,
  sheetName: string,
  id: string
): Promise<{ rowNumber: number; data: SheetRow } | null> {
  const rows = await fetchSheetRows(accessToken, spreadsheetId, sheetName);
  
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].id === id) {
      return { rowNumber: i + 2, data: rows[i] };
    }
  }
  
  return null;
}

export async function ensureSheetHeaders(
  accessToken: string,
  spreadsheetId: string,
  sheetName: string,
  headers: string[]
): Promise<void> {
  await createSheetIfNotExists(accessToken, spreadsheetId, sheetName);
  
  const response = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!A1:Z1`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Failed to check ${sheetName} headers`);
  }

  const data = await response.json();
  const existingHeaders = (data.values?.[0] as string[] | undefined) || [];

  if (existingHeaders.length === 0 || existingHeaders[0] === '') {
    await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!A1:${String.fromCharCode(64 + headers.length)}1?valueInputOption=RAW`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ values: [headers] }),
      }
    );
  }
}

export async function createSheetIfNotExists(
  accessToken: string,
  spreadsheetId: string,
  sheetName: string
): Promise<void> {
  const checkResponse = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!A1:A1`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  if (checkResponse.ok) return;

  await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        requests: [{
          addSheet: {
            properties: { title: sheetName }
          }
        }]
      })
    }
  );
}

export async function testConnection(
  accessToken: string,
  spreadsheetId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Products!A1:A1`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  if (response.ok) {
    return { success: true, message: 'Koneksi berhasil' };
  } else if (response.status === 403) {
    return { success: false, message: 'Akses ditolak. Pastikan spreadsheet sudah di-share ke service account' };
  } else if (response.status === 404) {
    return { success: false, message: 'Spreadsheet tidak ditemukan' };
  } else {
    return { success: false, message: `Error: ${response.status}` };
  }
}
